import type { Editor, EditorStateSnapshot } from '@tiptap/react'

import type { TextEditorTool } from '../types/tools.ts'

export type ToolState = {
  isActive?: boolean
  isDisabled?: boolean
}

export const defaultState: Record<TextEditorTool, ToolState> = {
  bold: {
    isActive: false,
    isDisabled: true,
  },
  italic: {
    isActive: false,
    isDisabled: true,
  },
  underline: {
    isActive: false,
    isDisabled: true,
  },
  strike: {
    isActive: false,
    isDisabled: true,
  },
  h1: {
    isActive: false,
    isDisabled: true,
  },
  h2: {
    isActive: false,
    isDisabled: true,
  },
  h3: {
    isActive: false,
    isDisabled: true,
  },
  h4: {
    isActive: false,
    isDisabled: true,
  },
  'bulleted-list': {
    isActive: false,
    isDisabled: true,
  },
  'numbered-list': {
    isActive: false,
    isDisabled: true,
  },
  'align-left': {
    isActive: false,
    isDisabled: true,
  },
  'align-center': {
    isActive: false,
    isDisabled: true,
  },
  'align-right': {
    isActive: false,
    isDisabled: true,
  },
  'align-justify': {
    isActive: false,
    isDisabled: true,
  },
  'indent-increase': {
    isActive: false,
    isDisabled: true,
  },
  'indent-decrease': {
    isActive: false,
    isDisabled: true,
  },
}

export const getEditorState = (ctx: EditorStateSnapshot<Editor | null>) => {
  if (!ctx.editor) {
    return defaultState
  }

  const state: Record<TextEditorTool, ToolState> = {
    bold: {
      isActive: ctx.editor.isActive('bold') ?? false,
      isDisabled: !ctx.editor.can().chain().toggleBold().run(),
    },
    italic: {
      isActive: ctx.editor.isActive('italic') ?? false,
      isDisabled: !ctx.editor.can().chain().toggleItalic().run(),
    },
    underline: {
      isActive: ctx.editor.isActive('underline') ?? false,
      isDisabled: !ctx.editor.can().chain().toggleUnderline().run(),
    },
    strike: {
      isActive: ctx.editor.isActive('strike') ?? false,
      isDisabled: !ctx.editor.can().chain().toggleStrike().run(),
    },
    h1: {
      isActive: ctx.editor.isActive('heading', { level: 1 }) ?? false,
    },
    h2: {
      isActive: ctx.editor.isActive('heading', { level: 2 }) ?? false,
    },
    h3: {
      isActive: ctx.editor.isActive('heading', { level: 3 }) ?? false,
    },
    h4: {
      isActive: ctx.editor.isActive('heading', { level: 4 }) ?? false,
    },
    'bulleted-list': {
      isActive: ctx.editor.isActive('bulletList') ?? false,
    },
    'numbered-list': {
      isActive: ctx.editor.isActive('orderedList') ?? false,
    },
    'align-left': {
      isActive: ctx.editor.isActive({ textAlign: 'left' }) ?? false,
      isDisabled: !ctx.editor.can().toggleTextAlign('left'),
    },
    'align-center': {
      isActive: ctx.editor.isActive({ textAlign: 'center' }) ?? false,
      isDisabled: !ctx.editor.can().toggleTextAlign('center'),
    },
    'align-right': {
      isActive: ctx.editor.isActive({ textAlign: 'right' }) ?? false,
      isDisabled: !ctx.editor.can().toggleTextAlign('right'),
    },
    'align-justify': {
      isActive: ctx.editor.isActive({ textAlign: 'justify' }) ?? false,
      isDisabled: !ctx.editor.can().toggleTextAlign('justify'),
    },
    'indent-increase': {
      isDisabled: !ctx.editor.can().sinkListItem('listItem'),
    },
    'indent-decrease': {
      isDisabled: !ctx.editor.can().liftListItem('listItem'),
    },
  }
  return state
}
